<template>
    <div>
        <div v-if="!price">
            <refill-prices :prices="creditRefillPricesCrypto" />
        </div>

        <div v-else-if="invoiceUrl" class="text-center my-4">
            <font-awesome-icon
                icon="fa-solid fa-shield-check"
                class="text-primary mb-3"
                size="2x"
            />
            <h5 class="mb-2">{{ $t("Complete your payment") }}</h5>
            <p class="text-secondary">
                {{
                    $t(
                        "Your secure checkout has opened in a new window. Finish there and your credits arrive automatically — if you don't see it, tap below."
                    )
                }}
            </p>
            <b-btn
                class="mt-3"
                @click="openCheckout"
                variant="primary"
                size="lg"
                block
                pill
            >
                {{ $t("Open Checkout") }}
            </b-btn>
        </div>

        <div v-else-if="error" class="text-center my-5">
            <div class="text-secondary mb-3">
                {{ $t("Something went wrong. Please try again.") }}
            </div>
            <b-btn @click="createInvoice" variant="primary" pill>
                {{ $t("Retry") }}
            </b-btn>
        </div>

        <div v-else-if="loading" class="text-center my-5">
            <b-spinner variant="primary" />
            <div class="mt-3 text-secondary">
                {{ $t("Opening checkout…") }}
            </div>
        </div>

        <div v-else>
            <refill-crypto-explainer />

            <b-btn
                :variant="dontShowAgain ? 'secondary' : 'outline-secondary'"
                class="mt-4 mb-3"
                @click="dontShowAgain = !dontShowAgain"
                size="lg"
                block
                pill
            >
                <font-awesome-icon
                    v-if="dontShowAgain"
                    icon="fa-solid fa-check"
                    class="mr-2"
                />
                {{
                    dontShowAgain
                        ? $t("Won't show this again")
                        : $t("Don't show me this again")
                }}
            </b-btn>

            <b-btn
                @click="pay"
                variant="primary"
                size="lg"
                block
                pill
            >
                {{ $t("Pay with Crypto") }}
            </b-btn>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

import RefillCryptoExplainer from "@/components/wallet/refill/RefillCryptoExplainer";
import RefillPrices from "@/components/wallet/refill/RefillPrices";

export default {
    data() {
        return {
            loading: false,
            error: false,
            invoiceUrl: null,
            dontShowAgain: false,
            unsubscribeToMutations: null,
        };
    },
    computed: {
        ...mapState("wallet", ["price"]),
        ...mapState("options", ["creditRefillPricesCrypto"]),
        ...mapState("paymentMethods", ["methods"]),
        familiar() {
            return this.methods?.find((method) => method.type === "crypto")
                ?.familiar;
        },
    },
    mounted() {
        this.unsubscribeToMutations = this.$store.subscribe((mutation) => {
            // Familiar customers skip the explainer — a package selection goes straight to checkout.
            if (
                mutation.type === "wallet/setPrice" &&
                mutation.payload &&
                this.familiar
            ) {
                this.createInvoice();
            }
        });
    },
    beforeDestroy() {
        if (this.unsubscribeToMutations) {
            this.unsubscribeToMutations();
        }
    },
    methods: {
        pay() {
            if (this.dontShowAgain) {
                this.api.post("/userFlag/cryptoFamiliar").catch(() => {});
            }
            this.createInvoice();
        },
        createInvoice() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.error = false;

            this.api
                .post("/nowpayments/create", {
                    priceId: this.price.id,
                })
                .then((response) => {
                    const invoiceUrl =
                        response?.data?.nowpaymentsInvoice?.invoiceUrl;

                    if (!invoiceUrl) {
                        throw new Error("no invoice url");
                    }

                    this.invoiceUrl = invoiceUrl;
                    this.openCheckout();
                })
                .catch(() => {
                    this.error = true;
                })
                .then(() => {
                    this.loading = false;
                });
        },
        openCheckout() {
            // Must route through system/openBrowser: document.location navigates the
            // webview and strands the Cordova app.
            this.$store.dispatch("system/openBrowser", this.invoiceUrl);
        },
    },
    components: {
        RefillCryptoExplainer,
        RefillPrices,
    },
};
</script>
